/** Production → Purchasing — generate / issue purchase orders. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSingleFlight } from "@/lib/manufacturing/useSingleFlight";
import {
  listManufacturingPlants,
  type MfgMaterial,
  type MfgPlant,
} from "@/lib/manufacturingClient";
import {
  acknowledgeManufacturingPlantRequest,
  createManufacturingPurchaseOrder,
  getManufacturingPurchaseOrder,
  getManufacturingPurchasingSettings,
  listManufacturingPurchaseOrders,
  listManufacturingVendors,
  updateManufacturingPurchaseOrder,
  type MfgPurchaseOrder,
  type MfgVendor,
} from "@/lib/manufacturing/purchaseOrderClient";
import { finalizePurchaseOrder } from "@/components/manufacturing/finalizePurchaseOrder";
import { issuePurchaseOrderAndDownloadPdf } from "@/components/manufacturing/issuePurchaseOrderAndDownloadPdf";
import {
  linesFromOrder,
  newDraftLine,
  serializeDraftLines,
  type PoDraftLine,
} from "@/components/manufacturing/purchaseOrderDraftState";
import { GeneratePurchaseOrderComposer } from "@/components/manufacturing/GeneratePurchaseOrderComposer";
import type { SavePlantDeliveryAddress } from "@/components/manufacturing/GeneratePurchaseOrderHeader";
import { resolvePoDraftLineMaterial } from "@/components/manufacturing/purchaseOrderDraftMaterials";
import { usePoCcEmails } from "@/components/manufacturing/usePoCcEmails";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { canIssueManufacturingPurchaseOrderFromHasRole } from "@/lib/manufacturing/plantMaterialRequestAccess";
import {
  purchaseOrderCanFinalize,
  purchaseOrderContentLocked,
} from "@/lib/manufacturing/purchaseOrderTypes";
import {
  clearPurchaseOrderOfflineDraft,
  loadPurchaseOrderOfflineDraft,
  savePurchaseOrderOfflineDraft,
} from "@/lib/manufacturing/purchaseOrderOfflineDraft";

export function GeneratePurchaseOrderBody(props: {
  enabled: boolean;
  materials: MfgMaterial[];
  onMaterialsRefresh: () => void;
  onOrdersChanged?: (orders: MfgPurchaseOrder[]) => void;
  reviewRequests?: boolean;
  focusRequestId?: string | null;
}) {
  const orgId = useScopedOrganizationId() ?? "";
  const auth = useAuth();
  const canIssue =
    auth?.status === "ready" &&
    canIssueManufacturingPurchaseOrderFromHasRole((...r) => auth.hasRole(...r));
  const [draftReady, setDraftReady] = useState(false);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [vendors, setVendors] = useState<MfgVendor[]>([]);
  const [orders, setOrders] = useState<MfgPurchaseOrder[]>([]);
  const [active, setActive] = useState<MfgPurchaseOrder | null>(null);
  const [plantId, setPlantId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [shipTo, setShipTo] = useState("");
  const [shipToOpen, setShipToOpen] = useState(false);
  const [poDate, setPoDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [confirmation, setConfirmation] = useState("");
  const cc = usePoCcEmails(active);
  const { ccEmails, setCcEmails, defaultPoCcEmails, defaultInvoiceCcEmails, mergeFromVendorContacts } = cc;
  const [lines, setLines] = useState<PoDraftLine[]>([newDraftLine()]);
  const [busy, setBusy] = useState(false);
  const flight = useSingleFlight();
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [expectedDeliveryOn, setExpectedDeliveryOn] = useState("");
  const savePlantDeliveryAddressRef = useRef<SavePlantDeliveryAddress>(async () => null);

  const plantName = useMemo(
    () => plants.find((p) => p.id === (active?.plant_id || plantId))?.name,
    [active?.plant_id, plantId, plants]
  );

  const loadLists = useCallback(async () => {
    const [p, v, o, s] = await Promise.all([
      listManufacturingPlants(),
      listManufacturingVendors({ activeOnly: true }),
      listManufacturingPurchaseOrders(),
      getManufacturingPurchasingSettings(),
    ]);
    if (p.ok) {
      const list = Array.isArray(p.data.plants) ? p.data.plants : [];
      setPlants(list);
      setPlantId((cur) => cur || list.find((x) => x.is_default)?.id || list[0]?.id || "");
    }
    if (v.ok) setVendors(Array.isArray(v.data.vendors) ? v.data.vendors : []);
    if (o.ok) {
      const list = Array.isArray(o.data.orders) ? o.data.orders : [];
      setOrders(list);
      props.onOrdersChanged?.(list);
    }
    if (s.ok) {
      const poCc = s.data.settings.default_po_cc_emails ?? [];
      const invCc = s.data.settings.default_invoice_cc_emails ?? [];
      cc.setDefaultPoCcEmails(poCc);
      cc.setDefaultInvoiceCcEmails(invCc);
    }
    if (!p.ok) setMsg({ text: p.error, ok: false });
    else if (!v.ok) setMsg({ text: v.error, ok: false });
    else if (!o.ok) setMsg({ text: o.error, ok: false });
  }, [props.onOrdersChanged]);

  useEffect(() => {
    if (props.enabled) void loadLists();
  }, [loadLists, props.enabled]);

  useEffect(() => {
    if (!orgId) {
      setDraftReady(true);
      return;
    }
    if (active) {
      setDraftReady(true);
      return;
    }
    const draft = loadPurchaseOrderOfflineDraft(orgId);
    if (draft) {
      setPlantId(draft.plant_id || plantId);
      setVendorId(draft.vendor_id);
      setShipTo(draft.ship_to);
      setPoDate(draft.po_date || poDate);
      setConfirmation(draft.confirmation);
      if (Array.isArray(draft.cc_emails)) setCcEmails(draft.cc_emails);
      if (draft.lines.length) setLines(draft.lines);
    }
    setDraftReady(true);
    // restore once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setDraftReady(true);
      return;
    }
    if (!draftReady || (active && active.status !== "draft")) return;
    savePurchaseOrderOfflineDraft({
      version: 1,
      saved_at: new Date().toISOString(),
      organization_id: orgId,
      plant_id: plantId,
      vendor_id: vendorId,
      vendor_name: vendors.find((v) => v.id === vendorId)?.name ?? "",
      ship_to: shipTo,
      po_date: poDate,
      confirmation,
      cc_emails: ccEmails,
      lines,
    });
  }, [active, ccEmails, confirmation, draftReady, lines, orgId, plantId, poDate, shipTo, vendorId, vendors]);

  function resetDraft() {
    setActive(null);
    setVendorId("");
    setShipTo("");
    setShipToOpen(false);
    setPoDate(new Date().toISOString().slice(0, 10));
    setConfirmation("");
    setCcEmails(defaultPoCcEmails);
    setExpectedDeliveryOn("");
    setLines([newDraftLine()]);
    if (orgId) clearPurchaseOrderOfflineDraft(orgId);
  }

  async function openOrder(poId: string) {
    setBusy(true);
    setMsg(null);
    const res = await getManufacturingPurchaseOrder(poId);
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    const order = res.data.order;
    setActive(order);
    setPlantId(order.plant_id);
    setVendorId(order.vendor_id ?? "");
    setShipTo(order.ship_to_override);
    setShipToOpen(Boolean(order.ship_to_override?.trim()));
    setPoDate(order.po_date);
    setConfirmation(order.confirmation_message);
    setCcEmails(order.cc_emails ?? []);
    setExpectedDeliveryOn(order.expected_delivery_on ?? "");
    setLines(linesFromOrder(order.lines));
  }

  async function saveDraft(): Promise<MfgPurchaseOrder | null> {
    if (!plantId) {
      setMsg({ text: "Select a plant.", ok: false });
      return null;
    }
    setBusy(true);
    setMsg(null);
    const resolved: PoDraftLine[] = [];
    for (const line of lines) {
      if (!line.material_name.trim()) continue;
      const r = await resolvePoDraftLineMaterial(line, {
        materials: props.materials,
        plantId,
        onMaterialCreated: props.onMaterialsRefresh,
      });
      if (!r.ok) {
        setBusy(false);
        setMsg({ text: r.error, ok: false });
        return null;
      }
      if (r.line) resolved.push(r.line);
    }
    if (resolved.length === 0) {
      setBusy(false);
      setMsg({ text: "Add at least one material line.", ok: false });
      return null;
    }
    setLines(resolved);
    const payload = {
      plant_id: plantId,
      vendor_id: vendorId || undefined,
      ship_to_override: shipTo,
      po_date: poDate,
      confirmation_message: confirmation || undefined,
      cc_emails: ccEmails,
      ...(active ? {} : { invoice_cc_emails: defaultInvoiceCcEmails }),
      lines: serializeDraftLines(resolved),
    };
    const res = active
      ? await updateManufacturingPurchaseOrder({ po_id: active.id, ...payload })
      : await createManufacturingPurchaseOrder(payload);
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return null;
    }
    setActive(res.data.order);
    setLines(linesFromOrder(res.data.order.lines));
    setCcEmails(res.data.order.cc_emails ?? ccEmails);
    setMsg({
      text: active ? `Saved PO ${res.data.order.po_number}` : `Created PO ${res.data.order.po_number}`,
      ok: true,
    });
    void loadLists();
    return res.data.order;
  }

  async function issueAndPdf() {
    const addressError = await savePlantDeliveryAddressRef.current();
    if (addressError) {
      setMsg({ text: addressError, ok: false });
      return;
    }
    return issuePurchaseOrderAndDownloadPdf({
      active,
      saveDraft,
      confirmation,
      ccEmails,
      expectedDeliveryOn,
      plantName,
      orgId,
      setBusy,
      setActive,
      setCcEmails,
      setMsg,
      loadLists: () => void loadLists(),
    });
  }

  async function acknowledgeRequest() {
    if (!active?.id) return;
    setBusy(true);
    const res = await acknowledgeManufacturingPlantRequest({ po_id: active.id });
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setActive(res.data.order);
    setMsg({ text: `Acknowledged request ${res.data.order.po_number}.`, ok: true });
    void loadLists();
  }

  async function reloadActive() {
    if (!active?.id) {
      void loadLists();
      return;
    }
    await openOrder(active.id);
  }

  useEffect(() => {
    if (!props.focusRequestId) return;
    void openOrder(props.focusRequestId);
    // open the request the inbox just picked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusRequestId]);

  const readOnlyHeader = Boolean(active && purchaseOrderContentLocked(active));
  const canFinalize = Boolean(canIssue && active && purchaseOrderCanFinalize(active));

  return (
    <GeneratePurchaseOrderComposer
      orders={orders}
      active={active}
      busy={busy}
      reviewRequests={props.reviewRequests}
      vendors={vendors}
      plants={plants}
      vendorId={vendorId}
      setVendorId={setVendorId}
      mergeVendorCc={mergeFromVendorContacts}
      plantId={plantId}
      setPlantId={setPlantId}
      shipTo={shipTo}
      setShipTo={setShipTo}
      shipToOpen={shipToOpen}
      setShipToOpen={setShipToOpen}
      poDate={poDate}
      setPoDate={setPoDate}
      enabled={props.enabled}
      readOnlyHeader={readOnlyHeader}
      materials={props.materials}
      lines={lines}
      setLines={setLines}
      confirmation={confirmation}
      setConfirmation={setConfirmation}
      ccEmails={ccEmails}
      setCcEmails={setCcEmails}
      msg={msg}
      setMsg={setMsg}
      canIssue={canIssue}
      expectedDeliveryOn={expectedDeliveryOn}
      setExpectedDeliveryOn={setExpectedDeliveryOn}
      defaultInvoiceCcEmails={defaultInvoiceCcEmails}
      onNew={resetDraft}
      onOpen={(id) => void openOrder(id)}
      onArchived={(order) => {
        if (active?.id === order.id) setActive(order);
        setMsg({ text: `Archived PO ${order.po_number}`, ok: true });
        void loadLists();
      }}
      onDeleted={(poId) => {
        setOrders((cur) => {
          const next = cur.filter((order) => order.id !== poId);
          props.onOrdersChanged?.(next);
          return next;
        });
        if (active?.id === poId) resetDraft();
        setMsg({ text: "Purchase order deleted.", ok: true });
      }}
      onVendorCreated={(vendor) => {
        setVendors((cur) => (cur.some((v) => v.id === vendor.id) ? cur : [...cur, vendor]));
        setVendorId(vendor.id);
      }}
      onPlantUpdated={(plant) => {
        setPlants((cur) => cur.map((p) => (p.id === plant.id ? { ...p, ...plant } : p)));
      }}
      savePlantDeliveryAddressRef={savePlantDeliveryAddressRef}
      onVendorsChanged={setVendors}
      onDictateApply={(next) => {
        if (next.vendorId) setVendorId(next.vendorId);
        if (next.vendorId) mergeFromVendorContacts(vendors.find((v) => v.id === next.vendorId)?.extra_contacts);
        if (next.lines?.length) setLines(next.lines);
      }}
      onSaveDraft={() => void flight(() => saveDraft())}
      onIssueAndPdf={() => void flight(() => issueAndPdf())}
      onFinalize={() =>
        void flight(() =>
          finalizePurchaseOrder({
            active,
            saveDraft,
            plantName,
            setBusy,
            setActive,
            setMsg,
            loadLists: () => void loadLists(),
          })
        )
      }
      canFinalize={canFinalize}
      finalized={Boolean(active?.finalized_at)}
      onAcknowledge={() => void flight(() => acknowledgeRequest())}
      onReload={() => void reloadActive()}
      onUpdated={(order) => setActive(order)}
    />
  );
}
